// "Trophy and award ideas", code-owned at the 2020 Contentful post's URL so
// the impressions and links it already has keep working.
// Claims stay inside the custom-trophy boundaries in .seo/truth.md: prices come
// from ./custom-trophy pricing (one source), no fixed turnaround or rush
// promise, plating is a metal surface and not solid metal.
// Examples are real studio projects (Nov 2025–Oct 2026, facts in .seo/truth.md
// "Trophy projects") plus earlier work already shown on /services/custom-trophy/.
// A client is named only when its trophy already appears on that page; others
// are described generically. No people's names or client prices.
const { pricing } = require('./custom-trophy')

const [paintedTier, platedTier] = pricing.tiers
const startingPrice = `$${pricing.from}`
const modelingFee = `$${pricing.modelingFee}`

const trophyAwardIdeas = {
  path: '/blog/best-unique-custom-trophy-and-award-ideas/',
  title: 'IDEA_TITLE',
  publishedDate: '2020-03-13',
  reviewedDate: '2026-11-02',
  reviewedDateLabel: 'November 2, 2026',
}

const description = `Unique trophy and award ideas from real custom trophies: fantasy leagues, perpetual, employee, funny and giant awards. From ${startingPrice}, no minimum.`
const answer = `The best unique trophy ideas turn something only your group would recognize into the trophy itself: a league’s inside joke, a player’s celebration pose, a company mascot or your product. PrintAWorld sculpts and 3D prints these in New York from a sketch, photo or AI image, from ${startingPrice} each with no minimum.`

const heroPhoto = {
  photo: 'rcCatsSet',
  caption:
    'Gold gamer cats in team jerseys and headsets, made for a company’s esports match. The set shipped 14 days after the order.',
}

const quickPicks = [
  {
    occasion: 'Fantasy football or rec league',
    section: 'fantasy-league-trophies',
    idea: 'The league’s inside joke or a player’s pose, on a wood base with plates',
    example: 'A hockey club’s “most discombobulated player” award',
  },
  {
    occasion: 'Perpetual trophy',
    section: 'perpetual-trophies',
    idea: 'A base sized for 20+ years of names, or an award that goes back every year',
    example: 'A golf beaver on a base built for 30 name plates',
  },
  {
    occasion: 'Employee or corporate award',
    section: 'company-employee-awards',
    idea: 'Your product, logo or industry turned into the trophy',
    example: 'Formic’s 10 robot-arm awards, shipped in 17 days',
  },
  {
    occasion: 'Tournament, esports or hackathon',
    section: 'tournament-esports-hackathon',
    idea: 'A mascot in team colors, plus minis for every winner',
    example: 'Gold gamer cats for an esports match',
  },
  {
    occasion: 'Brand mascot award',
    section: 'brand-mascot-awards',
    idea: 'The mascot itself, sculpted and painted to match',
    example: '45 lightning-god mascot trophies for a gym chain',
  },
  {
    occasion: 'Real person or celebration pose',
    section: 'likeness-pose-trophies',
    idea: 'The pose from a photo, with the face built from a headshot',
    example: 'An NBA player’s kneeling celebration for a fantasy league',
  },
  {
    occasion: 'Funny or last-place award',
    section: 'funny-last-place-awards',
    idea: 'The joke, played completely straight',
    example: 'A baby for Rookie of the Year',
  },
  {
    occasion: 'Sponsored event or media moment',
    section: 'giant-statement-trophies',
    idea: 'One big piece, 16 to 24 inches, in one bold finish',
    example: 'A 20-inch loving cup with the client’s crest',
  },
]

// `label` marks how sure the example is: Real example (made or in production),
// Earlier work (shown on the trophy page), Asked for (a real request that was
// quoted but not made, anonymized).
const ideaSections = [
  {
    id: 'fantasy-league-trophies',
    toc: 'Fantasy and rec leagues',
    heading: 'Fantasy football and rec league trophy ideas',
    intro:
      'League trophies work best when nobody outside the league would get the joke. They also get handed down, so plan the base for names.',
    ideas: [
      {
        title: 'Roast your own players',
        example:
          'A Chicago men’s hockey club ordered two awards for its season party. Defensive Player of the Year is a javelin thrower hurling a hockey stick. Offensive Player of the Year is, in the captain’s words, “the most discombobulated version of any hockey player in the history of the sport”, built from a real photo: jersey stuck on the shoulder pads, helmet in hand, gloves off, skate guards still on. Both are painted resin, about 12 inches on wood bases with nameplates. They shipped overnight the day before the party.',
        ask: 'Send the real photo behind the joke and describe it in a sentence. Then send notes on the 3D model. This club’s notes (the stick angle, socks over the shin pads, a messier jersey) are what made it.',
      },
      {
        title: 'A trophy in someone’s memory',
        example:
          'When a fantasy football league lost one of its members, it turned his team name into the trophy: a fish and a bomb, on a base with a nameplate in his honor.',
        ask: 'Send the team name, logo or joke the person was known for. It says more than a portrait.',
      },
      {
        title: 'A tournament cup used every year',
        example:
          'An Indigenous nation’s annual hockey tournament ordered two cups, for the men’s and women’s champions, in shiny metallic paint with a portrait raised on both sides, the wordmark on the base and space to engrave names later. They shipped to Canada eight days after the design was approved.',
        ask: 'Leave the date off a cup you reuse, and ask for engraving space for the years ahead.',
      },
      {
        title: 'The rivalry trophy',
        label: 'Asked for',
        example:
          'A group of friends who meet every year for a college football rivalry asked for a 12-inch football jersey split down the middle in both teams’ colors, on a walnut base with an inlaid plate and room for 24 yearly name plates.',
        ask: 'Give us the two team colors and the year the rivalry started. Say how many years of plates you want room for.',
      },
      {
        title: 'One design, every award',
        example:
          'A league’s champion, runner-up and rookie awards can share one sculpt with different plates, the way the hockey club’s two awards share a base style. The 3D modeling fee is paid once per design, however many copies you order.',
        ask: 'List every award name and say which ones share a design.',
      },
    ],
    link: [
      'More fantasy football trophy ideas and prices',
      '/services/custom-trophy/fantasy-football/',
    ],
  },
  {
    id: 'perpetual-trophies',
    toc: 'Perpetual trophies',
    heading: 'Perpetual trophy ideas that don’t run out of room',
    intro:
      'A perpetual trophy changes hands every year. The common mistake is a base that fills up. One fantasy league came to us because its old trophy had no room left after 13 seasons of champions.',
    ideas: [
      {
        title: 'Rows of plates on the sides',
        example:
          'That league’s new trophy has a big front plate for the title and side plates for every past champion, with room for six plates per side. The rivalry trophy above was planned for 24 years of plates from day one.',
        ask: 'Count the seasons you already have and the ones you want to add, and tell us at the start. The base is designed around that number.',
      },
      {
        title: 'A pun on the Stanley Cup',
        example:
          'A medical-device company’s education team runs a kidney-stone competition for medical residents, and its perpetual trophy is named as a pun on hockey’s most famous cup. It’s a gold-plated stone with a red kidney stone being chiseled out of it, on a stacked base: one big engraved plate for the title and extra tiers of blank plaques for the years ahead. The client chose plating over a rushed painted version, so it took about three months. Their European team has since asked for one too.',
        ask: 'Tell us the trophy’s name and the joke behind it. Ask for blank plaques on the base so future winners can be engraved locally.',
      },
      {
        title: 'The inside-joke cup',
        example:
          'A group of friends ordered a gold rotisserie-chicken trophy, named as a pun on the word, with winners engraved on three sides. A golf group’s perpetual is a realistic seated beaver on a tall base built for 25 to 30 laser-cut name plates; it shipped with 30 blank plates on tape so each year’s winner can be engraved locally.',
        ask: 'Tell us the name of the trophy and why. The story is the design brief.',
      },
      {
        title: 'Mix in a real object',
        example:
          'An oilfield-supply company’s store-of-the-year award, drawn from the client’s AI sketch, is two black oil-rig towers bridged by a beam that holds a real brass bell that swings. The towers and bell are metal on a printed base, 12 inches tall, with a swappable logo plate on the front and a new name plate on the back each year. It shipped about four weeks after the design was approved.',
        ask: 'If part of the idea is a real thing (a bell, a ball, a chain), we can build it in rather than print a copy.',
      },
      {
        title: 'The award that goes back every year',
        example:
          'The hockey club’s two awards carry no names at all. Winners keep them for a year and hand them back before the next party.',
        ask: 'If you engrave nothing, the trophy itself has to carry the story, so make the sculpt the joke.',
      },
      {
        title: 'The club logo, perpetual, with keepsakes',
        label: 'Asked for',
        example:
          'A golf club asked for its winged-sheep logo sculpted about 8 inches tall on a base with room for each year’s winner, plus small keepsakes the winners take home. They wanted to see full paint and a bronze-plated version side by side.',
        ask: 'Ask for minis at the same time as the main trophy. They use the same 3D model.',
      },
    ],
  },
  {
    id: 'company-employee-awards',
    toc: 'Company and employee awards',
    heading: 'Employee and corporate award ideas',
    intro:
      'The awards people keep on their desk are the ones that look like the company: the product, the industry, the inside language. A crystal block with a logo rarely gets that.',
    ideas: [
      {
        title: 'Your product as the trophy',
        photo: 'formicArm',
        caption:
          'Formic’s robot-arm award: resin arm in RAL orange and black on a weighted base.',
        example:
          'Formic installs industrial robots, so its 2026 customer awards are robot arms: 10 pieces, 8 inches tall, painted to Formic’s RAL 2009 orange and RAL 9005 black, clear-coated, with the logo UV printed on and a name plate for each winner. They shipped 17 days after Formic approved the design.',
        ask: 'Send product photos or CAD, and your brand colors as RAL or Pantone codes. Formic told us presentation mattered more than mechanical accuracy, which kept it printable.',
        link: ['Read the Formic case study', '/services/custom-trophy/#formic'],
      },
      {
        title: 'The sales club award',
        example:
          'A fulfillment-software company’s sales kickoff got five trophies of its 3D logo on weighted bases, in a chrome-effect paint, each plate reading the sales club’s name, the year and the winner. They shipped 10 days after the order was paid and went straight to the kickoff hotel.',
        ask: 'Give us the club name and the exact plate wording, and the delivery address if it isn’t your office.',
      },
      {
        title: 'A different trophy for every award',
        example:
          'A natural-gas utility supplier’s first year-end sales awards are five 8-inch trophies, each a different piece of the industry, one per award. Their own concept was too busy to print, so we drew one simplified concept image to guide all five. They’re being made now, and we hold them until the winners’ names arrive after the fiscal year closes.',
        ask: 'List the award names with one line on what each one celebrates. Names can come later; plates are the last thing we add.',
      },
      {
        title: 'Category awards people want to win',
        example:
          'A big tech company’s AI product team gives out about ten awards every couple of months: its 8-inch mascot in warm gold paint, weighted, on a round base with the category plate. The first seven were overnighted for a January event. The categories are the fun part: Driver, Ownership, Hill Climber, Proactive Leadership, Creative Innovation, Founder Mode and Multiplier. A farm brand asked for a four-piece set named Employee of the Year, No Quit, Quiet Force and Mastermind, with plates that swap out each year.',
        ask: 'Name the categories in your team’s own language. One sculpt with a different plate per category keeps it to one modeling fee.',
      },
      {
        title: 'The product in gold',
        example:
          'A fast-food brand’s First Pour Champion trophies are its coffee cup, 10 inches tall in a single gold finish with gold metal plates. Twenty shipped about four weeks after payment.',
        ask: 'One color across the whole piece is what lets twenty trophies ship in a month.',
      },
      {
        title: 'Awards for hitting sales levels',
        example:
          'A produce distributor hands out its logo as a weighted 6.5-inch “brick” in glitter gold and black when an employee reaches a sales level. They ordered 30, picked between two printed prototypes, and added a clear coat so the gold doesn’t scratch.',
        ask: 'Ask for a printed prototype when you’re ordering dozens. Two sizes side by side make the choice easy.',
      },
      {
        title: 'Staff-voted awards in school colors',
        example:
          'A California school district’s staff-voted PRIDE awards are stacked letters, the school name over the word PRIDE, painted maroon and gold on white bases with three-line engraved plates. Eight shipped about two weeks after payment, in 2025. The district later got the 3D file so students can print future ones.',
        ask: 'Send the school colors and the exact words. If you want the file afterwards, ask at the start.',
      },
      {
        title: 'An award based on one the boss liked',
        example:
          'A credit-scoring company’s top sales award copies the shape of a trophy its leader admired: an open lattice on a solid foot, all black, about 6 to 7 inches, with the logo, the award name and each winner’s name UV printed. We thickened the lattice into a stronger pattern so it survives. Twenty-two were made, shipped about 12 days after the form was approved.',
        ask: 'A photo of any trophy you like is a fine brief. We’ll tell you what has to change to print it.',
      },
      {
        title: 'Sales awards with attitude',
        photo: 'tribeGorillas',
        caption: 'Tribe’s Top Closer and Top Setter awards, in chrome.',
        example:
          'A sales team called Tribe asked for a chrome silverback gorilla and a chrome stallion. Thin printed horse legs would snap, so the final order was two 12-inch chrome gorillas on black bases, one plate reading Top Closer and one Top Setter.',
        ask: 'Tell us the finish you picture. If a shape won’t survive, we’ll say so before you pay and suggest a sturdier version.',
      },
      {
        title: 'Your logo, solid and heavy',
        example:
          'An AI-training company sent its emblem as a 3D file for six top-performer awards. To fit its budget we made them 6 inches tall in matte black (satin can look like a toy), printed solid so each weighs one to two pounds. They were picked up seven days after payment. For iHeartRadio’s earlier matte black logo awards, we printed each one hollow in two halves and filled it with poured cement so it feels heavy in the hand.',
        ask: 'Send the logo as a vector or 3D file if you have one. Matte black is the least expensive finish that still feels premium, and weight is what makes it feel expensive.',
      },
      {
        title: 'A founder or milestone award',
        example:
          'A one-year founder award was made from the client’s AI image: we redrew it as clean artwork, UV printed it in full color on cut acrylic, and mounted it with a nameplate. It was picked up a week after the order. A neurotech company asking about 5-, 10- and 15-year awards pointed us at the pink dog and the donut trophies on our own trophy page.',
        ask: 'For milestones, ask for one design with a different color or plate for each year mark, so you pay for one model.',
      },
      {
        title: 'The budget route: printed acrylic',
        example:
          'A health-tech team needed 28 awards on a tight budget, so they are cut acrylic with the logo UV printed in full color, about 6 by 4 inches. They took about three weeks.',
        ask: 'If the budget is closer to a catalog trophy, ask for flat acrylic with a printed logo instead of a sculpt.',
      },
      {
        title: 'A logo with its own shape',
        label: 'Earlier work',
        photo: 'goGetter',
        caption:
          'Go Getter awards for Dunkin’, with a name plate for each winner.',
        example:
          'Our trophy page shows earlier logo awards: Dunkin’s orange Go Getter donuts, tall red Spark letters with gold stars, and StreetEasy’s Rookie of the Year awards, shaped like a New York front door and stoop with the engraving on the steps.',
        ask: 'Send the logo file and say where each winner’s name goes.',
      },
    ],
  },
  {
    id: 'tournament-esports-hackathon',
    toc: 'Tournaments, esports, hackathons',
    heading: 'Tournament, esports and hackathon trophy ideas',
    intro:
      'Event trophies end up in every photo, so they should read from across the room. A mascot in team colors does that.',
    ideas: [
      {
        title: 'A gamer title trophy',
        example:
          'A creator’s gaming tournament gave out nine World’s Best Builder trophies: a panther head with a pickaxe and lightning, in weathered gold on marble bases, with each winner’s username and title on the plate. They were ready about three weeks after the deposit.',
        ask: 'Send the tournament art and the usernames. Weathered gold hides fingerprints better than shiny gold when trophies get passed around on stream.',
      },
      {
        title: 'A racing logo that stands up',
        example:
          'A kart-racing series turned its bull logo into a freestanding black sculpture, sturdier than a hanging plaque, with a plate for each class. Seven 14-inch first-place trophies shipped about four weeks after payment.',
        ask: 'List every class or division for the plates. One sculpt covers them all.',
      },
      {
        title: 'The company mascot as a gamer',
        photo: 'rcCat',
        caption:
          'An RC cat award: gold cat, red jersey, headset and flame wings on a twisted column.',
        example:
          'A software company’s offsite esports match got six 12-inch gold cats in red RC jerseys and gaming headsets, hand painted and airbrushed, with the logo on the front and the company name on the back like a real jersey. Their mockup had a halo and a microphone that would have snapped off, so we simplified them. The cats shipped 14 days after the order.',
        ask: 'Send your mockup or AI image and the team colors. Expect us to thicken anything thin.',
      },
      {
        title: 'Gold, silver and bronze from one model',
        example:
          'A creative agency ordered a first, second and third place set of an AI company’s character, in gold, silver and bronze metallic paint with matching plates, then came back for two more sets within a month. The first set shipped about two weeks after approval.',
        ask: 'One model, three finishes. Metallic paint is quicker than plating when the date is close.',
      },
      {
        title: 'Straight out of the game',
        example:
          'A video-game publisher’s tournament trophies were built from the game itself: a 15-inch monster head in weathered gold for first place and an in-game quest board in silver and bronze for second and third, with rough-edged “parchment” name plates. A dark wash over the gold took the plastic shine off. Each traveled in its own case.',
        ask: 'If you own the 3D models from the game or the brand, send them. Your artists’ files make the most accurate trophy.',
      },
      {
        title: 'Minis for every winner',
        example:
          'The same company came back for its hackathon: 150 gold mini cats about 4 inches tall, on weighted bases with a winner plate. Our designer reshaped the tail from their AI image, which was too thin to survive.',
        ask: 'For dozens or hundreds of minis, ask for one simple sculpt and one finish. Each extra paint color is hand work on every piece.',
      },
      {
        title: 'A hackathon mascot that gets its own stand',
        label: 'Earlier work',
        photo: 'duck',
        caption:
          'A matte black duck for a hackathon, on its own display block.',
        example:
          'An earlier hackathon trophy on our trophy page is a matte black duck on a grey display block.',
        ask: 'One color and one simple shape is the fastest kind of trophy to make.',
      },
    ],
  },
  {
    id: 'brand-mascot-awards',
    toc: 'Brand mascot awards',
    heading: 'Brand mascot and character award ideas',
    intro:
      'If the brand already has a character, the award is half designed. Send whatever you have, even a front view only; we design the sides and back.',
    ideas: [
      {
        title: 'The mascot, fifty times',
        example:
          'A national gym chain’s awards are its mascot as a lightning god on a cloud: 45 trophies for its annual conference, 10 of them added in the middle of production. Each is 12 inches, the figure on a 4–5 inch base, painted in full color with a laser-engraved metal plate; 30 carried a winner’s name and the rest just the year. It was the chain’s second year in a row, made from its own 3D files.',
        ask: 'Say how many will carry names and how many are spares, and when the names will be final. Extras can carry just the year.',
      },
      {
        title: 'A character award with a hat-tip',
        label: 'Earlier work',
        photo: 'telly',
        caption:
          'Two hat-tipping characters, painted and mounted on black bases.',
        example:
          'Earlier character awards on our trophy page include two magenta characters tipping their hats, and four painted Wanba Warriors characters paired on black plinths.',
        ask: 'Send the character art from the front and say which colors matter most.',
      },
      {
        title: 'The mascot as a gold statuette',
        label: 'Asked for',
        example:
          'A slots media brand asked for its mascot as an Oscar-style gold statuette, 10 of them, each shipped to a different winner. An events company asked for 35 painted 7-inch figures of its client’s brand character, with each recipient’s name on the base; we suggested a simpler pose so the face paint stays crisp.',
        ask: 'If the trophies go to different addresses, say so up front. We can ship each one to its winner.',
      },
    ],
  },
  {
    id: 'likeness-pose-trophies',
    toc: 'Likeness and pose toppers',
    heading: 'Trophies of a real person or a celebration pose',
    intro:
      'A pose comes from an action photo. A face needs its own clear photo, because a face that is small in an action shot doesn’t carry a likeness.',
    ideas: [
      {
        title: 'A player’s celebration pose',
        example:
          'The fantasy basketball league that ran out of room chose an NBA player’s famous kneeling celebration for its new trophy, sculpted in resin and painted aged bronze on a classic wood base. The face in the broadcast photo was too small, so we built the head from a sharp headshot and fitted it onto the sculpted body. The client approved the 3D model in November 2026 and it’s in production.',
        ask: 'Send the photo of the pose plus a close, front-facing photo of the face. A likeness takes more modeling than a logo, and the quote reflects that.',
      },
      {
        title: 'A promotion gift that looks like them',
        example:
          'For her husband’s promotion to managing director, a client ordered an 8-inch figure of him in a director’s chair, leaning forward with his chin in his hands, “Managing Director” on the chair back, on a wood base with a nameplate. The likeness came from his cartoon avatar on purpose, friendly rather than photo-real. She approved a printed proof before painting.',
        ask: 'Decide how real you want the face. A cartoon likeness from an avatar is quicker and often funnier than a realistic one.',
      },
      {
        title: 'A figure from your own team',
        label: 'Asked for',
        example:
          'A defense-tech company asked for two identical soldier figures for its awards ceremony, sending one photo for the uniform and another for the legs and boots.',
        ask: 'Send a photo of every angle you have. Front and side are enough to start.',
      },
    ],
  },
  {
    id: 'funny-last-place-awards',
    toc: 'Funny and last-place awards',
    heading: 'Funny award ideas and last-place trophies',
    intro:
      'Funny trophies work when they are made as seriously as the champion’s: same sculpting, same paint, same engraved plate.',
    ideas: [
      {
        title: 'Rookie of the Year is a baby',
        label: 'Earlier work',
        photo: 'freshlyBaked',
        caption:
          'Freshly Baked: Rookie of the Year, painted resin on engraved wood.',
        example:
          'One of the earlier awards on our trophy page is Freshly Baked, a pink baby on an engraved wood base, for Rookie of the Year. Another, for 1000heads’ Head of the Year, is a very long dog called Top Dog.',
        ask: 'Write the award title first. The best funny trophies are the title, sculpted literally.',
      },
      {
        title: 'A golden tooth',
        example:
          'A sports merchandise company’s team fest got a tooth trophy finished in gold leaf on a classic trophy base with a logo plate. It was picked up eight days after the client approved the 3D model.',
        ask: 'Odd objects are easy to model from two or three photos. Gold leaf gives a hand-made gold look without plating.',
      },
      {
        title: 'The near-miss award',
        example:
          'A mortgage company’s gag award is a stick-figure basketball player about to miss the shot, printed on acrylic and set on a printed base. It was made in two business days. Another client’s appreciation trophy is a silver ghost holding a notebook, for the colleague who quietly does everything.',
        ask: 'Flat acrylic art on a printed base is the fastest funny trophy there is.',
      },
      {
        title: 'A tech in-joke, in gold',
        example:
          'A software company’s award for a database conference is an old pager, about 6 inches, finished in gold on a black base, with the joke UV printed on its little screen.',
        ask: 'Pick an object your audience will recognize instantly. Printed graphics carry the punchline.',
      },
      {
        title: 'The turkey in a cage',
        label: 'Asked for',
        example:
          'A golf group’s Thanksgiving tournament asked for a turkey with a golf ball at its feet, locked inside a real metal cage on a wood base. We suggested aged bronze paint and building the cage from real metal rather than printing it.',
        ask: 'Mixing real parts (a cage, a chain, a golf ball) with a printed sculpt is fine. Send the part or a link to it.',
      },
      {
        title: 'The last-place trophy',
        label: 'Asked for',
        example:
          'A fantasy football league asked for its champion in gold on a throne, holding a football like a scepter, with the other 11 managers as gray figures in folding chairs looking up at him. Under the Champions plate sits a second plate for last place, with dated blanks for future seasons.',
        ask: 'Put the loser on the same trophy as the winner, or order a matching last-place piece. If it shares the base and plate style, the set looks planned.',
      },
    ],
  },
  {
    id: 'giant-statement-trophies',
    toc: 'Giant statement trophies',
    heading: 'Giant trophies for sponsored events (16 to 30 inches)',
    intro:
      'A big trophy is for a stage, a broadcast or a brand activation. Size and date pull against each other, so ask early.',
    ideas: [
      {
        title: 'A 20-inch cup with your crest',
        example:
          'A client sent a picture of a classic loving cup and their crest. We copied the cup at 20 inches in a bronze paint finish with the crest on the front.',
        ask: 'A photo of a cup you like plus your logo is enough to quote. Painted bronze or gold reads as metal from across a room.',
      },
      {
        title: 'The brand product, supersized',
        label: 'Asked for',
        example:
          'A beverage brand asked for a 30-inch trophy for a college football game: an oversized root beer mug with a gold football bursting out of sculpted mud and foam. With under three weeks to go, we recommended 24 inches at most and a single solid color for the mug, which kept the date possible. The brand’s client cancelled the event trophy, then came back to ask about a 20-inch version for a hockey game.',
        ask: 'Send the date first, then the size. At short notice we’ll offer a smaller size or a simpler finish rather than miss the event.',
      },
      {
        title: 'Tall and plated',
        label: 'Earlier work',
        photo: 'goldTowers',
        caption: 'Two tall gold-plated tower awards on round gold bases.',
        example:
          'Earlier statement pieces on our trophy page include tall gold-plated towers and the custom awards presented on the field for MLS.',
        ask: 'Very large pieces are printed in sections and joined, so the sculpt can be bigger than any single printer.',
      },
      {
        title: 'A one-of-a-kind career award',
        label: 'Asked for',
        example:
          'A recording-industry career award being designed now combines a miniature studio microphone, a vinyl record, headphones and a dancing couple on a walnut base about 13 inches wide. We redesigned the floating music notes to be sturdier.',
        ask: 'List every element you want, then rank them. On a big piece, the top two get the detail.',
      },
    ],
  },
]

const costRows = [
  {
    type: `${paintedTier.name}, ${paintedTier.size.replace('Standard size, ', '')}`,
    price: paintedTier.price,
    fit: 'Mascots, logos, league toppers, most company awards',
  },
  {
    type: platedTier.name,
    price: platedTier.price,
    fit: 'Gold, silver or chrome finishes, multi-color work, flagship awards',
  },
  {
    type: 'Trophies 16 inches and taller',
    price: platedTier.size.replace('16 inches and taller start at ', 'From '),
    fit: 'Giant championship and sponsored-event trophies',
  },
  {
    type: '3D modeling and design, once per design',
    price: `${modelingFee} for most projects`,
    fit: 'Every new design; a set of the same design pays it once',
  },
  ...pricing.extras
    .filter(([name]) => name !== '3D modeling and design')
    .map(([type, price]) => ({
      type,
      price,
      fit:
        type === 'Minimum order'
          ? 'One trophy is a normal order'
          : 'Before you commit to anything',
    })),
]

// Count, title and metaTitle follow the ideas list, so adding an idea never
// leaves a stale number in the headline.
const ideaCount = ideaSections.reduce(
  (n, section) => n + section.ideas.length,
  0
)
trophyAwardIdeas.title = `${ideaCount} unique trophy and award ideas, from real custom trophies`
const metaTitle = `${ideaCount} Unique Trophy and Award Ideas From Real Trophies`

const timing = [
  'Plan a month or more from your first message. That leaves room for the free mockup, your approval of the 3D model, a round of changes and the finishing.',
  'Real timelines from the past year: a fulfillment-software company’s five logo awards shipped 10 days after payment. Six esports cat trophies shipped 14 days after the order. Formic’s 10 robot-arm awards shipped 17 days after approval. Six matte black emblem awards, made from the client’s own 3D file, were picked up a week after payment, and a flat acrylic gag award took two business days.',
  'The slower ones are slower for a reason. A two-trophy hockey set with a detailed revision round shipped 31 days after sign-off. A gold-plated perpetual cup took about three months, because the client chose plating over a rushed painted version.',
  'What makes a short date possible: one simple shape, one color or a metallic, weathered gold or aged bronze paint, and a 3D file or a clear reference. A likeness or a very big piece needs more time, and real plating needs about six to eight weeks. If your date is close, ask anyway; we’ll tell you what size and finish fit before you pay.',
]

const faqs = [
  {
    question:
      'Where can I get a custom trophy made for my fantasy football league?',
    answer: `PrintAWorld, a studio in New York, designs and 3D prints one-of-a-kind fantasy football trophies and ships them anywhere in the U.S. Send a photo, sketch, AI image or the league’s inside joke; we send a free mockup, model it in 3D, then print, paint and mount it on a base with nameplates. Trophies start at ${startingPrice} with no minimum.`,
  },
  {
    question:
      'Who makes custom 3D printed trophies of a player’s celebration pose?',
    answer:
      'PrintAWorld sculpts celebration poses from a photo, such as an NBA player’s kneeling celebration for a fantasy basketball league. Send the action photo for the pose and a close, front-facing photo of the face. A face that is small in a broadcast shot doesn’t carry a likeness, so we build the head from the clear photo. You approve the 3D model before anything is printed.',
  },
  {
    question:
      'Can you make a trophy with a sculpted figure topper and a wood base with nameplates?',
    answer:
      'Yes, it’s our most common build: a resin figure, painted or plated, on a wood base with engraved plates. For a perpetual trophy we size the base for the number of years you want, and can include blank plates for future winners.',
  },
  {
    question: 'How much does a custom sculpted trophy cost?',
    answer: `At PrintAWorld, custom trophies start at ${startingPrice} each. Most painted resin trophies 8–12 inches tall cost ${paintedTier.price}; plated, gold-finish or large pieces run ${platedTier.price}, and ${platedTier.size.toLowerCase()}. Each design has a one-time 3D modeling fee, ${modelingFee} for most projects. The mockup is free and there is no minimum.`,
  },
  {
    question: 'Can you make a custom trophy in 2 to 3 weeks for a brand event?',
    answer:
      'Often, if the design allows it. Formic’s 10 awards shipped 17 days after approval, and six esports trophies shipped 14 days after the order. One shape, one paint finish and a clear reference make that possible; real plating, a likeness or a very large piece need longer. Send the event date first and we’ll tell you what fits before you pay.',
  },
  {
    question:
      'Can you make a large 24 to 30 inch trophy for a sponsored sports event?',
    answer: `Yes. Big trophies are printed in sections and joined, so the size isn’t limited by one printer, and ${platedTier.size.toLowerCase()}. With a short deadline we may recommend 24 inches and a single color rather than 30 inches with a complex paint job, so the trophy makes the event.`,
  },
  {
    question:
      'Can you make 10 custom 3D printed corporate awards in two weeks?',
    answer: `Ten of the same design is a normal order: the design is modeled once and the 3D modeling fee (${modelingFee} for most projects) is paid once, not per award. Formic’s 10 robot-arm awards shipped 17 days after approval, with name plates, and five logo awards for a sales kickoff shipped 10 days after payment. Tell us the date at the start; a simpler finish is usually how a two-week date works.`,
  },
  {
    question: 'Do you make hockey league championship trophies?',
    answer:
      'Yes. A Chicago men’s hockey club ordered its Defensive and Offensive Player of the Year awards from us, and an annual hockey tournament ordered two champions’ cups with room to engrave names. Perpetual cups with space for every season, and mini copies for each winner, work the same way.',
  },
  {
    question: 'Who are the best custom trophy makers in the US?',
    answer:
      'It depends on what you need. For many identical trophies with a logo on a budget, a catalog shop like Crown Awards is built for that. For cast metal, crystal or glass, look at a bespoke award maker. For a trophy sculpted from your own idea, such as a mascot, a pose or your product, a custom 3D printing studio like PrintAWorld in New York makes one-offs and small sets with no minimum.',
  },
  {
    question: 'Is a gold trophy solid metal?',
    answer:
      'No. Gold, silver and chrome trophies are metallic paint or real metal plating over the printed resin. Plating is a metal surface, not solid metal: it looks and feels like metal without the weight or cost of cast metal.',
  },
]

module.exports = {
  trophyAwardIdeas,
  metaTitle,
  description,
  answer,
  heroPhoto,
  quickPicks,
  ideaSections,
  costRows,
  timing,
  faqs,
  startingPrice,
}
